import type { DirectoryEntry } from "@/shared/hooks/useOrganizationDirectory";

/**
 * Turns a row's `organization_id` into one of FOUR states.
 *
 * The distinction that matters, and the reason this is a pure function with its own tests: **`none` and
 * `unknown` are opposite claims.** `none` says "this person belongs to no organization"; `unknown` says "we
 * could not find out which". Rendering both as an em-dash — which is what a `Record<string, string>` with `??
 * "—"` produces — merges an authoritative statement with an admission of ignorance. That is the same rule the
 * access screen applies by keeping `rolesUnavailable` apart from `rolesById`.
 */

export type OrganizationCellView =
  /** Resolved to a client organization. */
  | { kind: "resolved"; name: string }
  /** Resolved to the Coloca-owned tenant every INTERNAL user is pinned to. */
  | { kind: "root"; name: string }
  /** The row carries no organization at all. The column is NOT NULL, so this is a data defect — show it. */
  | { kind: "none" }
  /** An id we could not turn into a name. `reason` is `undefined` while it has simply not been fetched. */
  | { kind: "unknown"; id: string; reason?: "deleted" | "forbidden" | "error" };

/** Last 8 characters of the uuid — enough to recognise and to paste into a support ticket. */
export function organizationIdTail(id: string): string {
  return id.length <= 8 ? id : id.slice(-8);
}

export function resolveOrganizationCell(
  organizationId: string | null | undefined,
  entry: DirectoryEntry | undefined,
): OrganizationCellView {
  if (!organizationId) return { kind: "none" };

  if (!entry) return { kind: "unknown", id: organizationId };

  if (entry.kind === "unresolved") {
    return { kind: "unknown", id: organizationId, reason: entry.reason };
  }

  // `is_root` and never a name comparison: the migration matched the literal "Coloca Payments", but the row
  // is renameable through PATCH /internal/organizations/:id.
  return entry.isRoot ? { kind: "root", name: entry.name } : { kind: "resolved", name: entry.name };
}
